import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { X, Loader2 } from 'lucide-react'

interface UnsubscribeConfirmSheetProps {
  sender: string
  /** When true, the backend will perform the unsubscribe (HTTPS one-click or mailto via Resend). When false, the user must open the sender's page manually. */
  automatic: boolean
  busy: boolean
  onClose: () => void
  onConfirm: () => void
  /** Manual fallback target — opened in a new tab when automatic is false. */
  manualHref?: string | null
}

export default function UnsubscribeConfirmSheet({
  sender,
  automatic,
  busy,
  onClose,
  onConfirm,
  manualHref,
}: UnsubscribeConfirmSheetProps) {
  const { t } = useTranslation('chat')

  const bodyKey = automatic
    ? 'email.unsubscribe.bodyOneClick'
    : 'email.unsubscribe.bodyManual'

  const confirmLabel = automatic
    ? t('email.unsubscribe.confirm')
    : t('email.unsubscribe.openLink')

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center modal-backdrop bg-black/60"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-charcoal border-t border-slate-light/30 rounded-t-2xl p-5 pb-8 sm:border sm:rounded-2xl sm:pb-5 sm:shadow-2xl"
      >
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-base font-medium text-ivory">
            {t('email.unsubscribe.title', { sender })}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="ml-3 p-1.5 rounded-lg text-pearl/60 hover:text-ivory hover:bg-white/5 transition-colors flex-shrink-0"
            aria-label={t('email.unsubscribe.cancel')}
          >
            <X size={18} />
          </button>
        </div>

        <p className="text-sm text-pearl/70 leading-relaxed">
          {t(bodyKey)}
        </p>

        <p className="mt-3 text-xs text-pearl/45 leading-relaxed">
          {t('email.unsubscribe.footnote')}
        </p>

        <div className="mt-5 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 rounded-full text-sm text-pearl/70 hover:text-ivory hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            {t('email.unsubscribe.cancel')}
          </button>
          {automatic ? (
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-gold text-charcoal text-sm font-medium hover:bg-gold/90 transition-colors disabled:opacity-60"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              {confirmLabel}
            </button>
          ) : (
            <a
              href={manualHref ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onConfirm}
              className="px-4 py-2 rounded-full bg-gold text-charcoal text-sm font-medium hover:bg-gold/90 transition-colors"
            >
              {confirmLabel}
            </a>
          )}
        </div>
      </motion.div>
    </motion.div>
  )
}
